import { ViewTTY } from "viewtty";

const REWIND_EMOJI = "\u23EE\uFE0F";
const PLAY_EMOJI = "\u25B6\uFE0F";
const PAUSE_EMOJI = "\u23F8\uFE0F";

class ContentSwitcher extends HTMLElement {
  #labels: string[];
  #shadowRoot: ShadowRoot | null;
  #dynamicHeight: boolean;
  static observedAttributes = ["active", "dynamic-height"];
  constructor() {
    super();
    this.#labels = [];
    this.#shadowRoot = null;
    this.#dynamicHeight = false;
  }

  connectedCallback() {
    if (!this.#shadowRoot) {
      const slots = this.querySelectorAll("[slot]");
      for (let i = 0; i < slots.length; ++i) {
        const slotEl = slots[i];
        const attr = slotEl.getAttribute("slot");
        if (attr) {
          this.#labels.push(attr);
        }
      }
      this.#shadowRoot = this.attachShadow({ mode: "closed" });
      const documentStyles = document.querySelectorAll(
        'link[rel="stylesheet"],style',
      );
      for (let i = 0; i < documentStyles.length; ++i) {
        this.#shadowRoot.append(documentStyles[i].cloneNode(true));
      }

      const labelsEl = document.createElement("div");
      labelsEl.className = "content-switcher__labels";

      labelsEl.append(
        ...this.#labels.map((label) => {
          const button = document.createElement("button");
          button.className = "content-switcher__label";
          button.setAttribute("value", label);
          button.textContent = label;
          button.addEventListener("click", () => {
            this.setAttribute("active", label);
          });
          return button;
        }),
      );

      const contentEl = document.createElement("div");
      contentEl.className = "content-switcher__content";
      contentEl.append(
        ...this.#labels.map((label) => {
          const item = document.createElement("div");
          item.className = "content-switcher__item";
          item.setAttribute("data-name", label);
          const slot = document.createElement("slot");
          slot.className = "content-switcher__slot";
          slot.name = label;
          item.append(slot);
          return item;
        }),
      );

      this.#shadowRoot.append(labelsEl, contentEl);
      this.#updateActive(this.getAttribute("active"));
      this.#updateDynamicHeight();
    }
  }

  #updateActive(active: string | null) {
    if (!this.#shadowRoot) {
      return;
    }
    this.#shadowRoot
      .querySelectorAll(".content-switcher__label")
      .forEach((labelEl) => {
        const value = labelEl.getAttribute("value");
        labelEl.classList.toggle(
          "content-switcher__label--active",
          !!(value && active === value),
        );
      });
    this.#shadowRoot
      .querySelectorAll(".content-switcher__item")
      .forEach((labelEl) => {
        const value = labelEl.getAttribute("data-name");
        labelEl.classList.toggle(
          "content-switcher__item--active",
          !!(value && active === value),
        );
      });
  }

  #updateDynamicHeight() {
    const content = this.#shadowRoot?.querySelector(
      ".content-switcher__content",
    );
    if (content) {
      content.classList.toggle(
        "content-switcher__content--dynamic",
        this.#dynamicHeight,
      );
    }
  }

  disconnectedCallback() {}

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    if (name === "active") {
      this.#updateActive(newValue);
    }
    if (name === "dynamic-height") {
      this.#dynamicHeight = this.hasAttribute("dynamic-height");
      this.#updateDynamicHeight();
    }
  }
}
customElements.define("content-switcher", ContentSwitcher);

class TTYPlayer extends HTMLElement {
  private connected: boolean;
  private tty: any;
  private player: any;
  private playing: boolean;
  private container: HTMLDivElement;
  private buttonsContainer: HTMLDivElement;
  private pauseButton: HTMLButtonElement;
  private rewindButton: HTMLButtonElement;
  private xhr: XMLHttpRequest | null;

  static observedAttributes = ["cols", "rows", "src"];

  constructor() {
    super();
    this.tty = new ViewTTY.Terminal({
      cols: 80,
      rows: 25,
      noFocus: true,
      noEvents: true,
    });
    this.player = new ViewTTY.Player();
    this.player.addListener((event: any) => {
      if (event.type === "data") {
        this.tty.write(event.data.data);
      }
      if (event.type === "end") {
        this.tty.write(
          "\r\n\x1b[0m\x1b[38;5;46mTerminal recording complete.\x1b[0m",
        );
      }
    });
    this.container = document.createElement("div");
    this.container.className = "terminal-container";

    this.buttonsContainer = document.createElement("div");
    this.buttonsContainer.className = "tty-player__buttons";

    this.rewindButton = document.createElement("button");
    this.rewindButton.className = "tty-player__button";
    this.rewindButton.textContent = REWIND_EMOJI;

    this.pauseButton = document.createElement("button");
    this.pauseButton.className = "tty-player__button";
    this.pauseButton.textContent = PAUSE_EMOJI;

    this.buttonsContainer.appendChild(this.pauseButton);
    this.buttonsContainer.appendChild(this.rewindButton);

    this.xhr = null;
    this.playing = false;
    this.connected = false;
  }

  connectedCallback() {
    this.appendChild(this.container);
    this.appendChild(this.buttonsContainer);
    this.tty.open(this.container);
    this.tty.showCursor();
    this.connected = true;
    this.rewindButton.addEventListener("click", this.onRewind);
    this.pauseButton.addEventListener("click", this.onPause);
    this.resize();
    this.refetch();
  }

  disconnectedCallback() {
    this.player.pause();
    this.player.rewind();
    this.tty.reset();
    this.connected = false;
    this.rewindButton?.removeEventListener("click", this.onRewind);
    this.removeChild(this.buttonsContainer);
    this.removeChild(this.container);
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    if (!this.connected) {
      return;
    }
    switch (name) {
      case "cols":
      case "rows":
        this.resize();
        break;
      case "src":
        if (this.xhr) {
          this.xhr.abort();
        }
        this.refetch();
        break;
    }
  }

  refetch() {
    this.stop();
    this.rewindButton.disabled = true;
    this.pauseButton.disabled = true;
    this.tty.reset();
    this.tty.write("Fetching terminal recording... ");
    const src = this.getAttribute("src");
    if (!src) {
      this.tty.write(
        "\r\n\x1b[0m\x1b[38;5;196mNo recording src attribute found.\x1b[0m",
      );
      return;
    }
    let tickIndex = 0;
    let tickHandle = setInterval(() => {
      const spinner = "/|\\-"[tickIndex++ % 4];
      this.tty.write(`${spinner}\x1b[D`);
    }, 60);
    this.xhr = new XMLHttpRequest();
    const xhr = this.xhr;
    xhr.open("GET", src);
    xhr.responseType = "arraybuffer";
    xhr.onreadystatechange = () => {
      switch (xhr.readyState) {
        case xhr.UNSENT:
          // Happens when .abort() is called
          clearInterval(tickHandle);
          break;
        case xhr.DONE:
          clearInterval(tickHandle);
          if (xhr.status !== 200) {
            this.tty.write(
              "\r\n\x1b[0m\x1b[38;5;196mError loading terminal recording.\x1b[0m",
            );
          } else {
            try {
              // parse the response into chunks,
              var parser = new ViewTTY.Parser();
              var chunks = parser.parse(xhr.response);

              // load the chunks into the player,
              this.player.load(chunks);

              this.onRewind();
              this.rewindButton.disabled = false;
              this.pauseButton.disabled = false;
            } catch (e) {
              console.error(e);
              this.tty.write(
                "\r\n\x1b[0m\x1b[38;5;196mError parsing terminal recording.\x1b[0m",
              );
            }
          }
          break;
      }
    };
    xhr.send();
  }

  resize() {
    let cols = parseInt(this.getAttribute("cols") ?? "", 10);
    if (!isFinite(cols)) {
      cols = 80;
    }
    let rows = parseInt(this.getAttribute("rows") ?? "", 10);
    if (!isFinite(rows)) {
      rows = 25;
    }
    this.tty.resize(cols, rows);
  }

  stop() {
    this.playing = false;
    this.player.pause();
    this.player.rewind();
  }

  pause() {
    this.playing = false;
    this.player.pause();
    this.pauseButton.textContent = PLAY_EMOJI;
  }

  play() {
    this.playing = true;
    this.player.play();
    this.pauseButton.textContent = PAUSE_EMOJI;
  }

  onPause = () => {
    if (this.playing) {
      this.pause();
    } else {
      this.play();
    }
  };

  onRewind = () => {
    this.pause();
    this.player.rewind();
    this.tty.reset();
    this.tty.showCursor();
    this.play();
  };
}
customElements.define("tty-player", TTYPlayer);
